import { useState, useRef, useCallback } from "react";

const applyMomentum = (
  element: HTMLElement,
  initialVelocity: number,
  onComplete: () => void,
) => {
  let velocity = initialVelocity * 1000;
  const deceleration = 0.95;
  const minSpeed = 0.5;

  let lastTime = performance.now();

  const animate = (time: number) => {
    if (!element) {
      onComplete();
      return;
    }
    const deltaTime = time - lastTime;
    lastTime = time;

    element.scrollLeft -= velocity * (deltaTime / 1000);
    velocity *= deceleration;

    const isOutOfBound =
      element.scrollLeft <= 0 ||
      element.scrollLeft >= element.scrollWidth - element.clientWidth;

    if (Math.abs(velocity) > minSpeed && !isOutOfBound) {
      requestAnimationFrame(animate);
    } else {
      onComplete();
    }
  };

  requestAnimationFrame(animate);
};

export function useDraggableScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [isDragging, setIsDragging] = useState(false);

  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const velocity = useRef(0);
  const lastX = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);

  const cancelMomentumTracking = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const handleDragStart = useCallback((pageX: number) => {
    const element = ref.current;
    if (!element) return;

    cancelMomentumTracking();

    setIsDragging(true);
    startX.current = pageX - element.offsetLeft;
    scrollLeft.current = element.scrollLeft;
    velocity.current = 0;
    lastX.current = pageX;
    lastTimeRef.current = Date.now();
  }, []);

  const handleDragMove = useCallback(
    (pageX: number) => {
      const element = ref.current;
      if (!isDragging || !element) return;

      const currentTime = Date.now();
      const deltaTime = currentTime - (lastTimeRef.current ?? currentTime);

      if (deltaTime > 0) {
        const deltaX = pageX - lastX.current;
        velocity.current = deltaX / deltaTime;
      }

      const x = pageX - element.offsetLeft;
      const walk = x - startX.current;
      element.scrollLeft = scrollLeft.current - walk;

      lastX.current = pageX;
      lastTimeRef.current = currentTime;
    },
    [isDragging],
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const element = ref.current;
    if (element && Math.abs(velocity.current) > 0.1) {
      animationFrameRef.current = requestAnimationFrame(() => {
        applyMomentum(element, velocity.current, () => {
          animationFrameRef.current = null;
        });
      });
    }
  }, [isDragging]);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.pageX);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      handleDragMove(e.pageX);
    }
  };

  const onTouchStart = (e: React.TouchEvent) =>
    handleDragStart(e.touches[0].pageX);

  const onTouchMove = (e: React.TouchEvent) => {
    if (isDragging) {
      handleDragMove(e.touches[0].pageX);
    }
  };

  return {
    ref,
    isDragging,
    events: {
      onMouseDown,
      onMouseUp: handleDragEnd,
      onMouseLeave: handleDragEnd,
      onMouseMove,
      onTouchStart,
      onTouchEnd: handleDragEnd,
      onTouchMove,
    },
  };
}
